import { ChevronRightIcon, MonitorIcon, SmartphoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { focusVisibleRing } from '@/components/ui/constants';
import { cn } from '@/lib/utils';
import { timeFromNow } from '@/util/dateUtils';

import { WebSession } from './api';
import WebSessionStatus from './WebSessionStatus';

// Raw UA string rather than the parsed `name` — UA-parser outputs "Chrome on
// iOS" for mobile Chrome, which wouldn't match the regex; mobile browsers
// reliably include "Mobile" in their UA string.
const isMobile = (userAgent: string) => /iPhone|Android|Mobile/i.test(userAgent);

interface WebSessionItemProps {
  session: WebSession;
  onOpen: (session: WebSession) => void;
}

const WebSessionItem = ({ session, onOpen }: WebSessionItemProps) => {
  const { t } = useTranslation();
  const Icon = isMobile(session.user_agent) ? SmartphoneIcon : MonitorIcon;

  return (
    <button
      type="button"
      onClick={() => onOpen(session)}
      className={cn(
        'group flex w-full cursor-pointer items-center gap-4 rounded-lg border border-stroke bg-background px-4 py-4 text-left transition-colors hover:bg-bg-muted-50',
        focusVisibleRing
      )}
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border-[0.5px] border-stroke bg-bg-muted-light p-2">
        <div className="flex items-center justify-center rounded bg-background p-1 shadow-sm">
          <Icon className="h-6 w-6 text-foreground-muted" />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <div className="flex items-center gap-3">
          <span className="text-base leading-none font-medium text-foreground">{session.name}</span>
          <WebSessionStatus session={session} />
          {session.is_current && (
            <span className="text-xs leading-4 font-normal text-foreground-muted">
              {t('web_sessions.this_session_label')}
            </span>
          )}
        </div>
        <span className="truncate text-sm leading-5 font-normal text-foreground-muted">
          {session.location || t('web_sessions.unknown_location')}
          {' · '}
          {session.is_current || session.is_active
            ? t('web_sessions.active_now')
            : timeFromNow(session.last_seen_at)}
        </span>
      </div>

      <ChevronRightIcon
        size={24}
        className="shrink-0 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100"
      />
    </button>
  );
};

export default WebSessionItem;
